import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { logger } from "@artis/logger";

import { type ChartSettings, sampleConfig } from "../../../../../data/live-charts/config";
import type { QuestCandleData } from "../../../../../context/quest/api";
import { useQuestApi } from "../../../../../context/quest";
import { useOnWake } from "./useOnWake";

// Cap on how long the live stream may sit silent before its watchdog assumes the socket died and forces a reconnect. Tuned to the candle interval (a stream that has produced nothing for several candles' worth of time is almost certainly dead), with a floor so quiet markets don't churn and a ceiling so a dead slow chart still self-heals within a few minutes.
const liveIdleTimeout = (sampleSeconds: number) => Math.min(Math.max(sampleSeconds * 3 * 1000, 60_000), 300_000);

type Subscriber = (candle: QuestCandleData[], prev: QuestCandleData | null) => void;

// TanStack Query dedupes streaming queryFns by key, but the queryFn closure only captures one chart's cbRef so a shared stream wouldn't reach the other observers. This map fans each tick out to every chart currently subscribed to the key. Basically, if two charts share the same product/sample/period, they will share the same live stream and each tick will be sent to both charts' subscribers, otherwise one would stop working, or we'd need two separate streams for no reason.
const liveSubscribers = new Map<string, Set<Subscriber>>();

let liveStreamConnects = 0;
export const getLiveStreamConnects = () => liveStreamConnects;

export const useLive = (
  chart: Partial<Pick<ChartSettings, "productId" | "sampleTime" | "periodFrom" | "artisType" | "editable">> | undefined,
  f: Subscriber,
  enabled?: boolean,
) => {
  const cbRef = useRef(f);
  cbRef.current = f;
  const quest = useQuestApi();
  const client = useQueryClient();

  const enable = !!chart?.productId && !!chart?.sampleTime && !!chart?.periodFrom && enabled !== false;
  const key = `quest/live/${chart?.productId}/${chart?.sampleTime}/${chart?.periodFrom}`;
  const idleTimeoutMs = liveIdleTimeout(sampleConfig[chart?.sampleTime ?? "10s"].seconds);

  const currentKeyRef = useRef<string | null>(null);
  currentKeyRef.current = enable ? key : null;

  // Waking from sleep can leave the stream on a dead, half-open socket whose fetch never settles — so the query still looks "fetching" and neither the watchdog (not yet fired) nor React Query's focus refetch will touch it. Force a reconnect: cancelRefetch aborts the stale stream and opens a fresh one.
  useOnWake(({ reason }) => {
    if (!enable) return;
    logger.info("Reconnecting live chart stream after wake", { tags: ["lifecycle"], key, reason });
    client.resetQueries({ queryKey: [key], exact: true });
  });

  useEffect(() => {
    if (!enable) return;
    const subscriber: Subscriber = (candle, prev) => {
      if (currentKeyRef.current !== key) return;
      cbRef.current(candle, prev);
    };
    let set = liveSubscribers.get(key);
    if (!set) {
      set = new Set();
      liveSubscribers.set(key, set);
    }
    set.add(subscriber);
    return () => {
      const s = liveSubscribers.get(key);
      if (!s) return;
      s.delete(subscriber);
      if (s.size === 0) liveSubscribers.delete(key);
    };
  }, [key, enable]);

  return useQuery({
    enabled: enable,
    queryKey: [key],
    staleTime: 0,
    gcTime: 0,

    retry(failureCount, error) {
      console.log("live:retry", failureCount, error);
      return failureCount < 5;
    },
    queryFn: async (p) => {
      if (!chart || !chart.productId) return;

      liveStreamConnects++;

      const qry = quest.live(
        {
          id: chart.productId,
          period_from: chart.periodFrom || "",
          sample: chart.sampleTime || "10s",
          field: chart.editable ? "value" : "fv",
          isCalc: chart.artisType === "customer_curve",
          from: 0, // These seem fixed for live stream
          to: 1, // These seem fixed for live stream
        },
        { signal: p.signal, idleTimeoutMs },
      );

      let last: QuestCandleData | null = null;
      for await (const candle of qry) {
        const isNew = last ? last[0] < candle?.[0]?.[0] : false;
        const prev = isNew ? last : null;
        const subs = liveSubscribers.get(key);
        if (subs) for (const sub of subs) sub(candle, prev);
        last = candle?.[0] ?? null;
      }

      return null;
    },
  });
};
